import React, { useState, useEffect } from 'react';
import api from '../api';

const categories = [
    { id: 'you', label: 'You', example: 'dob, birthplace' },
    { id: 'io', label: 'I/O', example: 'email, phone' },
    { id: 'professional', label: 'Professional', example: 'role, skills' },
    { id: 'past', label: 'Past', example: 'education, childhood' },
    { id: 'future', label: 'Future', example: 'goals, dreams' },
    { id: 'likemap', label: 'LikeMap', example: 'music, food' },
    { id: 'human', label: 'Humans', example: 'family, friends' },
    { id: 'animal', label: 'Animals', example: 'pet, favorite animal' },
    { id: 'ai', label: 'AI', example: 'relationship, opinion' },
    { id: 'core', label: 'Core', example: 'values, beliefs' },
    { id: 'mind', label: 'Mind', example: 'traits, personality' },
    { id: 'condition', label: 'Condition', example: 'allergy, medication' },
    { id: 'other', label: 'Other', example: 'notes, miscellaneous' }
];

const Profile = () => {
    const [activeTab, setActiveTab] = useState('you');
    const [profileData, setProfileData] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [newValue, setNewValue] = useState('');
    const [newType, setNewType] = useState('');
    const activeCategory = categories.find(cat => cat.id === activeTab);

    // Helper to fetch profile data
    const fetchProfile = () => {
        api.get('/profile')
            .then(res => setProfileData(res.data))
            .catch(err => console.error('Error fetching profile:', err));
    };

    // Initial load
    useEffect(fetchProfile, []);

    const categorizedData = (categoryId) => {
        return profileData.filter(item => item.profile_key.startsWith(categoryId));
    };

    const startEdit = (item) => {
        setEditingItem(item);
        setNewValue(item.profile_value);
    };

    const saveEdit = () => {
        api.patch(`/profile/${editingItem.id}`, { profile_value: newValue })
            .then(() => {
                setProfileData(profileData.map(p => p.id === editingItem.id ? { ...p, profile_value: newValue } : p));
                setEditingItem(null);
                setNewValue('');
            })
            .catch(err => alert('Error updating profile data'));
    };

    const deleteItem = (id) => {
        api.delete(`/profile/${id}`)
            .then(() => setProfileData(profileData.filter(p => p.id !== id)))
            .catch(err => alert('Error deleting profile data'));
    };

    const addProfileItem = () => {
        if (!newType.trim() || !newValue.trim()) {
            alert('Please provide both type and value.');
            return;
        }

        api.post('/profile', {
            profile_key: `${activeTab}_${newType.trim().toLowerCase().replace(/\s+/g, '_')}`,
            profile_value: newValue
        })
            .then((res) => {
                setProfileData([...profileData, {
                    id: res.data.id, // Backend returns id ideally, otherwise refetch data
                    profile_key: `${activeTab}_${newType.trim().toLowerCase().replace(/\s+/g, '_')}`,
                    profile_value: newValue
                }]);
                setNewType('');
                setNewValue('');
            })
            .catch(err => {
                console.error('Error adding profile item:', err);
                alert('Failed to add profile item.');
            });
    };

    return (
        <div className="terminal min-h-screen px-4 py-6 font-mono text-green-300">
            <div className="max-w-6xl mx-auto px-4">

                <h2 className="text-green-400 text-2xl mb-6 text-center">USER PROFILE</h2>

                {/* Refresh button */}
                <div className="flex justify-center mb-4">
                    <button
                        onClick={fetchProfile}
                        className="btn-primary text-xs px-3 py-1"
                    >
                        🔄 Refresh Profile
                    </button>
                </div>

                {/* Section Tabs */}
                <div className="flex flex-wrap gap-2 justify-center mb-6">
                    {categories.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`px-3 py-1 border rounded font-mono text-sm transition-all ${activeTab === tab.id
                                ? 'border-green-500 bg-green-700 text-black font-bold'
                                : 'border-green-700 hover:border-green-500 text-black-400'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Add New Entry */}
                <div className="border border-green-500 bg-black rounded p-6 mb-10 shadow-lg">
                    <h3 className="text-green-400 text-lg mb-3 text-center">
                        ➕ Add Profile Data for {activeCategory.label}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
                        <input
                            placeholder={`Type (e.g., ${activeCategory.example})`}
                            value={newType}
                            onChange={(e) => setNewType(e.target.value)}
                            className="input-terminal"
                        />
                        <input
                            placeholder="Value"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            className="input-terminal"
                        />
                    </div>
                    <button onClick={addProfileItem} className="btn-primary w-full">
                        Add Profile Data
                    </button>
                </div>

                {/* Display Entries */}
                <div className="border border-green-500 bg-black rounded p-6 shadow-lg">
                    <h3 className="text-green-400 text-lg mb-4">
                        🧩 {activeCategory.label} — Memory Entries
                    </h3>
                    {categorizedData(activeTab).length === 0 ? (
                        <p className="text-sm text-green-500 italic opacity-70 text-center">No entries yet. Add your first memory above.</p>
                    ) : (
                        <ul className="space-y-4">
                            {categorizedData(activeTab).map(item => (
                                <li key={item.id} className="border border-green-700 p-4 rounded">
                                    {editingItem?.id === item.id ? (
                                        <div className="flex flex-col sm:flex-row gap-3 items-center">
                                            <input
                                                value={newValue}
                                                onChange={(e) => setNewValue(e.target.value)}
                                                className="input-terminal w-full"
                                            />
                                            <div className="flex gap-2">
                                                <button onClick={saveEdit} className="btn-primary text-sm">Save</button>
                                                <button onClick={() => setEditingItem(null)} className="text-sm border border-red-500 text-red-400 px-3 py-1 rounded hover:bg-red-700 hover:text-white">
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                            <div className="text-green-200 text-sm">{item.profile_value}</div>
                                            <div className="flex gap-2 text-xs">
                                                <button
                                                    onClick={() => startEdit(item)}
                                                    className="border border-green-500 px-3 py-1 rounded hover:bg-green-600 hover:text-black"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => deleteItem(item.id)}
                                                    className="px-3 py-1 rounded border border-red-600 bg-red-600 text-white transition-all hover:bg-transparent hover:text-red-500 hover:border-red-500"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Profile;